// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Stack,
  Divider,
  Alert,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  Star,
  Info,
  Edit,
  ContentCopy,
  PowerSettingsNew,
  Download,
  MoreVert
} from '@mui/icons-material';

/**
 * QACriteriaDetail Component
 * 
 * Displays full details of a QA criteria including:
 * - Metadata (name, team, department, version, status)
 * - Categories with weight distribution
 * - Metrics per category with details
 */
const QACriteriaDetail = ({ 
  criteria, 
  loading = false, 
  onEdit,
  onDuplicate,
  onToggleActive,
  onSetDefault,
  onExport
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDuplicate = () => {
    handleMenuClose();
    if (onDuplicate) onDuplicate();
  };

  const handleToggleActive = () => {
    handleMenuClose();
    if (onToggleActive) onToggleActive();
  };

  const handleSetDefault = () => {
    handleMenuClose();
    if (onSetDefault) onSetDefault();
  };

  const handleExport = () => {
    handleMenuClose();
    if (onExport) onExport();
  };
  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Loading criteria details...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!criteria) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Please select a QA criteria from the dropdown above to view details.
        </Typography>
      </Alert>
    );
  }

  // Calculate total weights for validation
  const totalCategoryWeight = criteria.categories?.reduce(
    (sum, cat) => sum + (cat.weight_percentage || 0), 
    0
  ) || 0;

  const isBooleanCriteria = criteria.criteria_type === 'boolean';

  return (
    <Box>
      {/* Criteria Metadata Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {criteria.name}
                {criteria.is_default && (
                  <Chip
                    icon={<Star />}
                    label="Default"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {criteria.description || 'No description provided'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={criteria.is_active ? <CheckCircle /> : <Cancel />}
                label={criteria.is_active ? 'Active' : 'Inactive'}
                color={criteria.is_active ? 'success' : 'default'}
                size="small"
                onClick={onToggleActive ? () => onToggleActive() : undefined}
                sx={onToggleActive ? { cursor: 'pointer' } : undefined}
                title={onToggleActive ? (criteria.is_active ? 'Click to deactivate' : 'Click to activate') : undefined}
              />
              {onEdit && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Edit />}
                    onClick={onEdit}
                  >
                    Edit
                  </Button>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    title="More actions"
                  >
                    <MoreVert />
                  </IconButton>
                </>
              )}
            </Stack>
          </Box>

          {/* Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleDuplicate}>
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleToggleActive}>
              <ListItemIcon>
                <PowerSettingsNew fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {criteria.is_active ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>

            {criteria.is_active && !criteria.is_default && (
              <MenuItem onClick={handleSetDefault}>
                <ListItemIcon>
                  <Star fontSize="small" />
                </ListItemIcon>
                <ListItemText>Set as Default</ListItemText>
              </MenuItem>
            )}

            <Divider />

            <MenuItem onClick={handleExport}>
              <ListItemIcon>
                <Download fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export JSON</ListItemText>
            </MenuItem>
          </Menu>

          <Divider sx={{ my: 2 }} />

          {/* Metadata Grid */}
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Team
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {criteria.team?.name || criteria.team_name || 'N/A'}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">
                Visibility
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {criteria.visibility === 'private' && '🔒 Private'}
                {criteria.visibility === 'team' && '👥 Team'}
                {criteria.visibility === 'public' && '📋 Template'}
                {!criteria.visibility && 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Version
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {criteria.version || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Metrics
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {criteria.total_metrics || 0}
              </Typography>
            </Box>

            {criteria.criteria_type !== 'boolean' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Weight
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {totalCategoryWeight}%
                  {totalCategoryWeight !== 100 && (
                    <Chip 
                      label="Invalid" 
                      size="small" 
                      color="error" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Categories
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {criteria.categories_count || 0}
              </Typography>
            </Box>
          </Stack>

          {/* Dates */}
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {criteria.created_at ? new Date(criteria.created_at).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {criteria.updated_at ? new Date(criteria.updated_at).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* QA Context Settings */}
      {(criteria.conversation_type || criteria.participants || criteria.conversation_objective || 
        criteria.ideal_approach || criteria.target_audience || criteria.context_notes) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              QA Context Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Conversation context used for AI analysis
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {/* Context Fields Grid */}
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {criteria.conversation_type && (
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Conversation Type
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {criteria.conversation_type}
                    </Typography>
                  </Box>
                )}

                {criteria.participants && (
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Participants
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {criteria.participants}
                    </Typography>
                  </Box>
                )}

                {criteria.target_audience && (
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Target Audience
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {criteria.target_audience}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Conversation Objective */}
              {criteria.conversation_objective && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Conversation Objective
                  </Typography>
                  <Typography variant="body2">
                    {criteria.conversation_objective}
                  </Typography>
                </Box>
              )}

              {/* Ideal Approach */}
              {criteria.ideal_approach && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Ideal Approach
                  </Typography>
                  <Typography variant="body2">
                    {criteria.ideal_approach}
                  </Typography>
                </Box>
              )}

              {/* Additional Context Notes */}
              {criteria.context_notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Additional Context Notes
                  </Typography>
                  <Typography variant="body2">
                    {criteria.context_notes}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Categories & Metrics - Accordion View */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Categories & Metrics
      </Typography>

      {!criteria.categories || criteria.categories.length === 0 ? (
        <Alert severity="warning">
          No categories defined for this criteria.
        </Alert>
      ) : (
        criteria.categories.map((category, index) => {
          const totalMetricWeight = isBooleanCriteria
            ? 0
            : category.metrics?.reduce(
                (sum, metric) => sum + (metric.weight_percentage || 0),
                0
              ) || 0;
          const categoryAllocation = category.weight_percentage ?? 0;
          const metricWeightsMismatch =
            !isBooleanCriteria &&
            (category.metrics?.length || 0) > 0 &&
            totalMetricWeight !== categoryAllocation;

          return (
            <Accordion 
              key={category.id || index}
              defaultExpanded={index === 0}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {category.category_name}
                    </Typography>
                    {category.description && (
                      <Typography variant="caption" color="text.secondary">
                        {category.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {!isBooleanCriteria && (
                      <Chip
                        label={`${category.weight_percentage}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={`${category.metrics_count || 0} metrics`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {/* Weight distribution applies to matrix (scoring) criteria only */}
                {!isBooleanCriteria && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Weight Distribution
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={category.weight_percentage} 
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {category.weight_percentage}% of total criteria
                    </Typography>
                  </Box>
                )}

                {/* Metrics Table */}
                {!category.metrics || category.metrics.length === 0 ? (
                  <Alert severity="warning" icon={<Info />}>
                    No metrics defined for this category.
                  </Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width="5%">#</TableCell>
                            <TableCell width={isBooleanCriteria ? '18%' : '28%'}>Metric Name</TableCell>
                            <TableCell width={isBooleanCriteria ? '42%' : '35%'}>Description</TableCell>
                            {isBooleanCriteria && (
                              <TableCell width="12%">Mandatory</TableCell>
                            )}
                            {!isBooleanCriteria && (
                              <TableCell width="12%">Weight</TableCell>
                            )}
                            <TableCell width={isBooleanCriteria ? '23%' : '15%'}>Keywords</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {category.metrics.map((metric, metricIndex) => (
                            <TableRow key={metric.id || metricIndex}>
                              <TableCell>{metricIndex + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {metric.metric_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {metric.description || '-'}
                                </Typography>
                              </TableCell>
                              {isBooleanCriteria && (
                                <TableCell>
                                  <Chip
                                    label={metric.is_mandatory ? 'Yes' : 'No'}
                                    size="small"
                                    color={metric.is_mandatory ? 'primary' : 'default'}
                                    variant={metric.is_mandatory ? 'filled' : 'outlined'}
                                  />
                                </TableCell>
                              )}
                              {!isBooleanCriteria && (
                                <TableCell>
                                  <Chip
                                    label={`${metric.weight_percentage}%`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                {metric.keywords && metric.keywords.length > 0 ? (
                                  <Typography variant="caption">
                                    {metric.keywords.slice(0, 2).join(', ')}
                                    {metric.keywords.length > 2 && '...'}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    None
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Category weight validation — matrix criteria only */}
                    {!isBooleanCriteria && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Total metric weights: {totalMetricWeight}% (category allocation: {categoryAllocation}%)
                        </Typography>
                        {metricWeightsMismatch && (
                          <Chip
                            label={`Warning: Metrics total ${totalMetricWeight}% but category is ${categoryAllocation}%`}
                            size="small"
                            color="warning"
                            icon={<Info />}
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* QA Context Preview - EXACT AI Prompt */}
      {criteria.high_level_context && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            🪄 QA Context Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is the EXACT context that will be sent to AI for Sentiment Analysis, QA Scoring, and Improvement Suggestions
          </Typography>
          
          <Card>
            <CardContent>
              <Box 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: '500px',
                  overflow: 'auto',
                  border: '1px solid #e0e0e0'
                }}
              >
                {criteria.high_level_context}
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default QACriteriaDetail;
